import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import { AXIOS_API_INSTANCE, SERVICE_ORDER_SERVICE_COLOR_MAP, SERVICE_ORDER_SERVICE_FRIENDLY_NAMES } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Alert, Box, Checkbox, Chip, FormControlLabel, IconButton } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { LoadingBackdropIndeterminate } from "../../LoadingBackdrop"
import dayjs from "dayjs"
import { useTrackedUserOpenServiceOrderWorkHistory } from "../../UserState"

export default function StopWorkModal({
    serviceOrder,
    serviceOrderWorkHistory,
    open,
    closeModal,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [, setOpenServiceOrderWorkHistory] = useTrackedUserOpenServiceOrderWorkHistory();
    const [overheadNote, setOverheadNote] = React.useState("")
    const [diagnosticNote, setDiagnosticNote] = React.useState("")
    const [diagnosticMinutes, setDiagnosticMinutes] = React.useState(0)
    const [saving, setSaving] = React.useState(false)
    const [serviceData, setServiceData] = React.useState({})
    const [totalMinutes, setTotalMinutes] = React.useState(0)

    const handleChange = (id, prop) => (event) => {
        if (['recorded_minutes'].includes(prop)) {
            serviceData[id][prop] = Math.max(parseInt(event.target.value || 0), 0)
        } else if (prop === 'mark_complete') {
            serviceData[id][prop] = event.target.checked
        } else {
            serviceData[id][prop] = event.target.value
        }
        setServiceData({ ...serviceData })
    }

    const save = (event) => {
        setSaving(true)
        const stop_work = {
            service_data: serviceData,
            overhead_note: overheadNote.trim(),
            diagnostic_minutes: diagnosticMinutes,
            diagnostic_note: diagnosticNote.trim(),
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.id}/stop_work`, stop_work).then(async (new_serviceOrder) => {
                enqueueSnackbar('Successfully ended work', { variant: 'success' })
                setOpenServiceOrderWorkHistory(undefined)
                var response = new_serviceOrder.data.data
                const mark_completeds = Object.keys(serviceData).filter((service_id) => serviceData[service_id].mark_complete)
                // loop through all the services and mark them as completed if the checkbox is checked
                if (mark_completeds.length > 0) {
                    const then_callback = (service_id) => (new_serviceOrder) => {
                        enqueueSnackbar(`Successfully marked service ${serviceOrder.service_order_services.find((s) => s.id.toString() === service_id)?.name} as Completed`, { variant: 'success' })
                        response = new_serviceOrder.data.data
                    }
                    for (let i = 0; i < mark_completeds.length; i++) {
                        const service_id = mark_completeds[i]
                        await AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.id}/change_service_status`, {
                            serviceorderservice_id: service_id,
                            status: 'COMPLETED'
                        }).then(then_callback(service_id)).catch((error) => {
                            enqueueSnackbar(`Failed to mark service ${serviceOrder.service_order_services.find((s) => s.id.toString() === service_id)?.name} as Completed <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                        })
                    }
                }
                setSaving(false)
                closeModal(response)
            }).catch((error) => {
                setSaving(false)
                enqueueSnackbar(`Failed to end work <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    React.useEffect(() => {
        if (open) {
            const total_minutes = dayjs.utc().diff(dayjs.utc(serviceOrderWorkHistory.start_time), 'minutes')
            let new_serviceData = {}
            let diagnostic_minutes = 0
            const number_in_progress = serviceOrder.service_order_services.filter((service) => service.status === 'IN_PROGRESS').length
            if (number_in_progress === 0) {
                diagnostic_minutes = total_minutes
            }
            serviceOrder.service_order_services.forEach((service) => {
                let minutes = 0
                if (service.status === 'IN_PROGRESS') {
                    minutes = Math.floor(total_minutes / number_in_progress)
                }
                new_serviceData[service.id] = {
                    note: "",
                    recorded_minutes: minutes,
                    mark_complete: false
                }
            })
            setTotalMinutes(total_minutes)
            setServiceData(new_serviceData)
            setDiagnosticMinutes(diagnostic_minutes)
            setOverheadNote('')
            setDiagnosticNote('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const enteredMinutes = Object.values(serviceData).reduce((accumulator, item) => accumulator + parseInt(item.recorded_minutes), 0) + diagnosticMinutes

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                maxWidth={'lg'}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <LoadingBackdropIndeterminate
                    open={saving}
                    text={"Stopping Work"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>Stop Work</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Grid container direction="column" spacing={2} sx={{ minWidth: '300px' }}>
                            <Grid size="grow">
                                <h3>Are you sure you want to Stop Work on this service order?</h3>
                            </Grid>
                            <Grid container spacing={2} size="grow">
                                <Grid size={4}>
                                    <span>Service</span>
                                </Grid>
                                <Grid size={2}>
                                    <span>Minutes</span>
                                </Grid>
                                <Grid size={6}>
                                    <span>Comments (optional)</span>
                                </Grid>
                                <Grid container size={12} spacing={2} alignItems={"center"}>
                                    <Grid size={4}>
                                        <span>Diagnostic</span>
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField
                                            margin="dense"
                                            id="diagnostic_minutes"
                                            hiddenLabel
                                            size="small"
                                            type="number"
                                            fullWidth
                                            variant="filled"
                                            value={parseInt(diagnosticMinutes).toString()}
                                            onChange={(e) => setDiagnosticMinutes(Math.max(parseInt(e.target.value || 0), 0))}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            margin="dense"
                                            id="diagnostic_note"
                                            hiddenLabel
                                            size="small"
                                            type="text"
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            variant="filled"
                                            value={diagnosticNote}
                                            onChange={(e => setDiagnosticNote(e.target.value))}
                                        />
                                    </Grid>
                                </Grid>
                                {serviceOrder.service_order_services.map((service) => (
                                    <Grid container key={service.id} size={12} spacing={2} alignItems={"center"}>
                                        <Grid container size={4} alignItems={"center"}>
                                            <Grid container size="auto" direction='column' alignItems='center' spacing={0}>
                                                <Grid>
                                                    <Chip label={SERVICE_ORDER_SERVICE_FRIENDLY_NAMES[service.status]} color={SERVICE_ORDER_SERVICE_COLOR_MAP[service.status] || 'primary'} />
                                                </Grid>
                                                {service.status === 'IN_PROGRESS' &&
                                                    <Grid>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={serviceData[service.id]?.mark_complete ?? false}
                                                                    onChange={handleChange(service.id, "mark_complete")}
                                                                    size="small"
                                                                    color={SERVICE_ORDER_SERVICE_COLOR_MAP['COMPLETED']}
                                                                    sx={{ fontSize: '0.6rem', }}
                                                                    slotProps={{
                                                                        input: {
                                                                            'aria-label': 'Mark Complete',
                                                                            padding: '0px',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            slotProps={{
                                                                typography: {
                                                                    'fontSize': '0.6rem',
                                                                }
                                                            }}
                                                            label="Mark Complete"
                                                        />
                                                    </Grid>
                                                }
                                            </Grid>
                                            <Grid size="grow">
                                                <span>{service.name}</span>
                                            </Grid>
                                        </Grid>
                                        <Grid size={2}>
                                            <TextField
                                                margin="dense"
                                                id="recorded_minutes"
                                                hiddenLabel
                                                size="small"
                                                type="number"
                                                fullWidth
                                                variant="filled"
                                                value={parseInt(serviceData[service.id]?.recorded_minutes).toString()}
                                                onChange={handleChange(service.id, "recorded_minutes")}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <TextField
                                                margin="dense"
                                                id="note"
                                                hiddenLabel
                                                size="small"
                                                type="text"
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                variant="filled"
                                                value={serviceData[service.id]?.note ?? ''}
                                                onChange={handleChange(service.id, "note")}
                                            />
                                        </Grid>
                                    </Grid>
                                ))}
                                <Grid size={12}>
                                    <hr />
                                </Grid>

                                <Grid container spacing={2} alignItems={"center"} size={12}>
                                    {enteredMinutes > totalMinutes &&
                                        <Grid size={12}>
                                            <Alert severity="warning">Time elapsed since start work is {totalMinutes}m. You've entered {enteredMinutes} minutes</Alert>
                                        </Grid>
                                    }
                                    <Grid size={4}>
                                        <span><u>Overhead</u></span>
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField
                                            margin="dense"
                                            id="recorded_minutes"
                                            hiddenLabel
                                            size="small"
                                            type="number"
                                            fullWidth
                                            variant="outlined"
                                            value={Math.max(0, totalMinutes - enteredMinutes).toString()}
                                            slotProps={{
                                                input: {
                                                    readOnly: true,
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            margin="dense"
                                            id="note"
                                            hiddenLabel
                                            size="small"
                                            type="text"
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            variant="filled"
                                            value={overheadNote}
                                            onChange={(event) => setOverheadNote(event.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={save}>
                                Confirm
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div >)
    );
}
